import { featureCollection, type Point, point } from '@turf/helpers';
import type { MapMouseEvent } from 'mapbox-gl';
import { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useGroupHover from '@alltrails/core/hooks/useGroupHover';
import { DEFAULT_WAYPOINT_CATEGORY_UID } from '@alltrails/waypoints/utils/waypointCategories';
import useMap from '../../hooks/useMap';
import { useAppDispatch, useAppSelector } from '../../redux';
import {
  clearViewingWaypointId,
  clearWaypointEditingState,
  setViewingWaypointId,
  setWaypointEditingState,
  updateWaypointPosition
} from '../../redux/customRoute';
import { updateCursor } from '../../redux/reducer';
import { WAYPOINT_ZOOM } from '../../utils/customRoutes/constants';
import { customRouteWaypointsId } from '../../utils/layers';
import WaypointPinLayer from './WaypointPinLayer';

export const SOURCE_ID = 'custom-route-waypoints-source';

const CustomRouteWaypoints = () => {
  const dispatch = useAppDispatch();
  const map = useMap();
  const intl = useIntl();
  const { activeWaypointId, selectedWaypointId, viewingWaypointId, waypoints, isEditingWaypoint, draftWaypoint } = useAppSelector(state => ({
    activeWaypointId: state.customRoute.activeWaypointId,
    selectedWaypointId: state.customRoute.selectedWaypointId,
    viewingWaypointId: state.customRoute.viewingWaypointId,
    waypoints: state.customRoute.route.waypoints,
    isEditingWaypoint: state.customRoute.isEditingWaypoint,
    draftWaypoint: state.customRoute.draftWaypoint
  }));

  const selectedWaypoint = useMemo(() => {
    if (selectedWaypointId === undefined) {
      return undefined;
    }
    const waypoint = waypoints.find(({ id }) => id === selectedWaypointId);
    if (!waypoint) {
      return undefined;
    }

    if (draftWaypoint && draftWaypoint.id === selectedWaypointId) {
      return {
        ...waypoint,
        ...draftWaypoint
      };
    }
    return waypoint;
  }, [selectedWaypointId, waypoints, draftWaypoint]);

  useEffect(() => {
    if (!map || selectedWaypoint === undefined || selectedWaypoint === null) {
      return;
    }

    const { latitude, longitude } = selectedWaypoint;
    const pointCoords: [number, number] = [longitude, latitude];

    map.easeTo({ center: pointCoords, zoom: WAYPOINT_ZOOM });
  }, [map, selectedWaypoint]);

  const onSelectedDragEnd = useCallback(
    (event: { lngLat: { lng: number; lat: number } }) => {
      if (selectedWaypoint === undefined) {
        return;
      }
      // When dragging while the edit drawer is open, only update position
      // without adding to undo stack. The undo stack will be updated when the user saves.
      dispatch(
        updateWaypointPosition({
          id: selectedWaypoint.id,
          latitude: Number(event.lngLat.lat.toFixed(6)),
          longitude: Number(event.lngLat.lng.toFixed(6))
        })
      );
    },
    [dispatch, selectedWaypoint]
  );

  const onHover = useCallback(
    event => {
      if (isEditingWaypoint) {
        return;
      }

      const id = event.id;
      if (id !== undefined) {
        dispatch(setWaypointEditingState({ activeId: id }));
        dispatch(updateCursor('pointer'));
      }
    },
    [isEditingWaypoint, dispatch]
  );

  const onHoverEnd = useCallback(() => {
    if (isEditingWaypoint) {
      return;
    }

    if (activeWaypointId !== undefined) {
      dispatch(clearWaypointEditingState());
      dispatch(updateCursor('unset'));
    }
  }, [dispatch, activeWaypointId, isEditingWaypoint]);

  const { onMouseEnter, onMouseLeave } = useGroupHover(onHover, onHoverEnd, true /* isMouseMove */, 50 /* debounceTime */);

  const waypointsData = useMemo(
    () =>
      featureCollection<Point>(
        waypoints
          .map((waypoint, index) => ({ waypoint, index }))
          .filter(({ waypoint }) => waypoint.id !== selectedWaypointId)
          .map(({ waypoint, index }) => {
            const { id, name, showTitle, latitude, longitude, waypointCategory } = waypoint;
            const title = showTitle
              ? name === ''
                ? intl.formatMessage({ defaultMessage: 'Waypoint {name}' }, { name: index + 1 })
                : name
              : undefined;
            const categoryUid = waypointCategory?.uid ?? DEFAULT_WAYPOINT_CATEGORY_UID;
            // No `activeOrSelected` here — composed below into `composedActiveWaypointId` and consumed
            // by WaypointPinLayer via a Mapbox expression.
            return point([longitude, latitude], { title, id, categoryUid });
          })
      ),
    [waypoints, selectedWaypointId, intl]
  );

  // `viewingWaypointId` keeps the currently-viewed pin enlarged even after the user hovers
  // away from it, matching the visual selection state shown in the sidebar.
  const composedActiveWaypointId = activeWaypointId ?? viewingWaypointId;

  const onMouseMove = useCallback(
    (event: MapMouseEvent) => {
      if (isEditingWaypoint) {
        return;
      }

      if (onMouseEnter && event.features) {
        const spot = event.features[0].properties;
        onMouseEnter(spot);
      }
    },
    [onMouseEnter, isEditingWaypoint]
  );

  const onClick = useCallback(
    (event: MapMouseEvent) => {
      if (!event.features) {
        return;
      }
      event.preventDefault(); // Allows us to detect when a user clicks on the map but NOT on this layer
      if (isEditingWaypoint) {
        return;
      }
      const id = event.features[0]?.properties?.id;
      if (id === undefined) {
        return;
      }

      if (viewingWaypointId === id) {
        dispatch(clearViewingWaypointId());
        return;
      }

      dispatch(setViewingWaypointId({ id, entryPoint: 'custom_routes', sourceView: 'map_pin' }));
    },
    [dispatch, isEditingWaypoint, viewingWaypointId]
  );

  const selectedWaypointDescriptor = useMemo(() => {
    if (!selectedWaypoint) {
      return undefined;
    }
    const renderedTitle = selectedWaypoint.showTitle ? (
      selectedWaypoint.name === '' ? (
        <FormattedMessage defaultMessage="Waypoint {name}" values={{ name: (selectedWaypoint.id ?? 0) + 1 }} />
      ) : (
        selectedWaypoint.name
      )
    ) : undefined;
    return {
      id: selectedWaypoint.id,
      latitude: selectedWaypoint.latitude,
      longitude: selectedWaypoint.longitude,
      title: renderedTitle,
      categoryUid: selectedWaypoint.waypointCategory?.uid
    };
  }, [selectedWaypoint]);

  return (
    <WaypointPinLayer
      activeWaypointId={composedActiveWaypointId}
      isDraggable={isEditingWaypoint}
      layerId={customRouteWaypointsId}
      onClick={onClick}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
      onSelectedDragEnd={onSelectedDragEnd}
      source={{ type: 'geojson', data: waypointsData }}
      sourceId={SOURCE_ID}
      variant="user"
      waypointMarkerProperties={selectedWaypointDescriptor}
    />
  );
};

export default CustomRouteWaypoints;
